export type ProductMode = 'demo' | 'standalone' | 'server-client';
export type LicenseState = 'unlicensed' | 'trial' | 'licensed' | 'expired' | 'offline-grace' | 'time-unverified' | 'device-mismatch';
export type LicenseType = 'standalone' | 'server-client';
export type ExternalSqlProvider = 'postgresql' | 'mysql' | 'mssql' | 'oracle' | 'odbc';
export type LicenseTimeStatusState = 'valid' | 'invalid' | 'unavailable' | 'device-mismatch';

export interface LicenseEntitlements {
  licenseId: string;
  licensedUserId: string;
  licensedUserName: string;
  qualifiedInvoiceNumber?: string | null;
  licenseType: LicenseType;
  standalone: boolean;
  serverClient: boolean;
  externalSqlProviders: ExternalSqlProvider[];
  offlineGraceDays: number;
  validFrom: string;
  validUntil: string | null;
  licenseRequestId?: string | null;
  deviceLockHash?: string | null;
  deviceLabel?: string | null;
  trustedTimeUnixSeconds?: number;
  trustedTimeServer?: string;
}

export interface LicenseTimeStatus {
  state: LicenseTimeStatusState;
  reason?: string;
  trustedTimeUnixSeconds?: number;
  trustedTimeServer?: string;
}

export interface ProductModeInfo {
  mode: ProductMode;
  licenseState: LicenseState;
  entitlements: LicenseEntitlements | null;
}

const PRODUCT_MODE_KEY = 'revenue:product_mode';
const LICENSE_KEY = 'revenue:license_entitlements';
const LICENSE_TIME_STATUS_KEY = 'revenue:license_time_status';

function isDesktopRuntime(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

function webLimitedProductionMode(): boolean {
  return !isDesktopRuntime() && import.meta.env.VITE_WEB_LIMITED_PRODUCTION === 'true';
}

function forceWebDemoMode(): boolean {
  return !isDesktopRuntime() && import.meta.env.VITE_WEB_DEMO_MODE !== 'false';
}

function readStoredProductMode(): ProductMode | null {
  try {
    const raw = localStorage.getItem(PRODUCT_MODE_KEY);
    return raw === 'demo' || raw === 'standalone' || raw === 'server-client' ? raw : null;
  } catch {
    return null;
  }
}

function readStoredEntitlements(): LicenseEntitlements | null {
  try {
    const raw = localStorage.getItem(LICENSE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LicenseEntitlements>;
    if (
      typeof parsed.licenseId !== 'string' ||
      typeof parsed.licensedUserId !== 'string' ||
      typeof parsed.licensedUserName !== 'string' ||
      (parsed.licenseType !== 'standalone' && parsed.licenseType !== 'server-client') ||
      typeof parsed.validFrom !== 'string'
    ) {
      return null;
    }
    return {
      licenseId: parsed.licenseId,
      licensedUserId: parsed.licensedUserId,
      licensedUserName: parsed.licensedUserName,
      qualifiedInvoiceNumber: typeof parsed.qualifiedInvoiceNumber === 'string' ? parsed.qualifiedInvoiceNumber : null,
      licenseType: parsed.licenseType,
      standalone: Boolean(parsed.standalone),
      serverClient: Boolean(parsed.serverClient),
      externalSqlProviders: Array.isArray(parsed.externalSqlProviders) ? parsed.externalSqlProviders : [],
      offlineGraceDays: Number.isFinite(parsed.offlineGraceDays) ? Number(parsed.offlineGraceDays) : 0,
      validFrom: parsed.validFrom,
      validUntil: typeof parsed.validUntil === 'string' ? parsed.validUntil : null,
      licenseRequestId: typeof parsed.licenseRequestId === 'string' ? parsed.licenseRequestId : null,
      deviceLockHash: typeof parsed.deviceLockHash === 'string' ? parsed.deviceLockHash : null,
      deviceLabel: typeof parsed.deviceLabel === 'string' ? parsed.deviceLabel : null,
      trustedTimeUnixSeconds: Number.isFinite(parsed.trustedTimeUnixSeconds) ? Number(parsed.trustedTimeUnixSeconds) : undefined,
      trustedTimeServer: typeof parsed.trustedTimeServer === 'string' ? parsed.trustedTimeServer : undefined,
    };
  } catch {
    return null;
  }
}

function readLicenseTimeStatus(): LicenseTimeStatus | null {
  try {
    const raw = localStorage.getItem(LICENSE_TIME_STATUS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LicenseTimeStatus>;
    if (
      parsed.state !== 'valid' &&
      parsed.state !== 'invalid' &&
      parsed.state !== 'unavailable' &&
      parsed.state !== 'device-mismatch'
    ) return null;
    return {
      state: parsed.state,
      reason: typeof parsed.reason === 'string' ? parsed.reason : undefined,
      trustedTimeUnixSeconds: Number.isFinite(parsed.trustedTimeUnixSeconds) ? Number(parsed.trustedTimeUnixSeconds) : undefined,
      trustedTimeServer: typeof parsed.trustedTimeServer === 'string' ? parsed.trustedTimeServer : undefined,
    };
  } catch {
    return null;
  }
}

function envProductMode(): ProductMode | null {
  const value = (import.meta.env.VITE_PRODUCT_MODE ?? '').trim();
  return value === 'demo' || value === 'standalone' || value === 'server-client' ? value : null;
}

export function getProductModeInfo(): ProductModeInfo {
  if (webLimitedProductionMode()) {
    return {
      mode: 'standalone',
      licenseState: 'licensed',
      entitlements: {
        licenseId: 'web-limited-production',
        licensedUserId: 'limited-environment',
        licensedUserName: '限定環境',
        qualifiedInvoiceNumber: null,
        licenseType: 'standalone',
        standalone: true,
        serverClient: false,
        externalSqlProviders: [],
        offlineGraceDays: 0,
        validFrom: '1970-01-01T00:00:00.000Z',
        validUntil: null,
      },
    };
  }

  if (forceWebDemoMode()) {
    return {
      mode: 'demo',
      licenseState: 'unlicensed',
      entitlements: null,
    };
  }

  const entitlements = readStoredEntitlements();
  const timeStatus = entitlements ? readLicenseTimeStatus() : null;
  const licenseState: LicenseState = !entitlements
    ? 'unlicensed'
    : timeStatus?.state === 'valid'
    ? 'licensed'
    : timeStatus?.state === 'invalid'
    ? 'expired'
    : timeStatus?.state === 'device-mismatch'
    ? 'device-mismatch'
    : 'time-unverified';
  const licensed = licenseState === 'licensed';
  const requestedMode = licensed
    ? envProductMode() ?? readStoredProductMode() ?? entitlements?.licenseType ?? 'demo'
    : 'demo';
  const mode = !licensed || !entitlements
    ? 'demo'
    : requestedMode === 'server-client' && !entitlements.serverClient
    ? 'standalone'
    : requestedMode === 'standalone' && !entitlements.standalone
    ? entitlements.licenseType
    : requestedMode;
  return {
    mode,
    licenseState,
    entitlements,
  };
}

export function saveProductMode(mode: ProductMode): void {
  localStorage.setItem(PRODUCT_MODE_KEY, mode);
}

export function saveLicenseEntitlements(entitlements: LicenseEntitlements): void {
  localStorage.setItem(LICENSE_KEY, JSON.stringify(entitlements));
}

export function saveLicenseTimeStatus(status: LicenseTimeStatus): void {
  localStorage.setItem(LICENSE_TIME_STATUS_KEY, JSON.stringify(status));
}

export function loadLicenseEntitlements(): LicenseEntitlements | null {
  return readStoredEntitlements();
}

export function loadLicenseTimeStatus(): LicenseTimeStatus | null {
  return readLicenseTimeStatus();
}

export function clearLicenseEntitlements(): void {
  localStorage.removeItem(LICENSE_KEY);
  localStorage.removeItem(LICENSE_TIME_STATUS_KEY);
}

export function isDemoMode(): boolean {
  return getProductModeInfo().mode === 'demo';
}

export function isLicensedMode(): boolean {
  return getProductModeInfo().licenseState === 'licensed';
}

export function isWebLimitedProductionMode(): boolean {
  return webLimitedProductionMode();
}

export function productModeLabel(mode: ProductMode): string {
  if (mode === 'standalone') return 'スタンドアロン';
  if (mode === 'server-client') return 'サーバクライアント';
  return 'デモ';
}

export function licenseStateLabel(state: LicenseState): string {
  if (state === 'licensed') return 'ライセンス認証済み';
  if (state === 'trial') return '試用中';
  if (state === 'expired') return '期限切れ';
  if (state === 'offline-grace') return 'オフライン猶予';
  if (state === 'time-unverified') return 'NTP時刻未確認';
  if (state === 'device-mismatch') return '端末不一致';
  return '未ライセンス';
}
